import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { usePage } from '@inertiajs/react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { Role } from '../../Data/Role';

const REQUIRED_STEPS = [
	'basics',
	'ownership',
	'employees',
	'accounts',
	'pc-carriers',
	'lh-carriers',
	'producer-bobs',
	'non-recurring-revenues',
	'non-recurring-expenses',
	'agency-information',
	'carrier-loss-ratios',
	'carrier-retention-rates',
];

const ValuationStatusBanner = () => {
	const { valuationId, valuationStatus, valuationAnalystEmail, completedSteps, user } = usePage<any>().props;
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	if (!valuationId || !valuationStatus) return null;
	if (user?.role === Role.admin) return null;

	const completed: string[] = completedSteps ?? [];
	const allComplete = REQUIRED_STEPS.every((step) => completed.includes(step));

	const handleSubmit = () => {
		setSubmitting(true);
		router.post(`/valuation-form/${valuationId}/submit`, {}, {
			onFinish: () => {
				setSubmitting(false);
				setShowConfirm(false);
			},
		});
	};

	if (valuationStatus === 'In Progress') {
		return (
			<>
				<Alert variant='info' className='d-flex justify-content-between align-items-center mb-0'>
					<span>
						{allComplete
							? 'All sections are complete. Ready to submit your valuation for review.'
							: 'Complete all sections to submit your valuation for review.'}
					</span>
					<Button
						variant='primary'
						size='sm'
						className='rounded-pill ms-3'
						disabled={!allComplete}
						onClick={() => setShowConfirm(true)}
					>
						Submit for Review
					</Button>
				</Alert>

				<Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
					<Modal.Header closeButton>
						<Modal.Title>Submit Valuation</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						Are you sure you want to submit this valuation for review? Once submitted,
						you will no longer be able to make changes. Any additional updates must be
						communicated via email to your valuation agent.
					</Modal.Body>
					<Modal.Footer>
						<Button variant='secondary' onClick={() => setShowConfirm(false)}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleSubmit} disabled={submitting}>
							{submitting ? 'Submitting...' : 'Confirm Submit'}
						</Button>
					</Modal.Footer>
				</Modal>
			</>
		);
	}

	const contactMessage = valuationAnalystEmail ? (
		<>
			If this form requires any changes, please email{' '}
			<a href={`mailto:${valuationAnalystEmail}`}>{valuationAnalystEmail}</a>.
		</>
	) : (
		<>
			If this form requires any changes, please{' '}
			<a href='https://iavaluations.com/contact/' target='_blank'>contact us</a>.
		</>
	);

	if (valuationStatus === 'Under Review') {
		return (
			<Alert variant='warning' className='mb-0'>
				This valuation is currently under review. {contactMessage}
			</Alert>
		);
	}

	if (valuationStatus === 'Complete') {
		return (
			<Alert variant='success' className='mb-0'>
				This valuation is complete. {contactMessage}
			</Alert>
		);
	}

	return null;
};

export default ValuationStatusBanner;
